import { useState } from 'react'
import { Icon } from './Icon'
import type { ProjectSummary, Workspace } from '../api'
import type { Theme } from '../theme'
import { useDialog } from '../useDialog'

/**
 * The top-left row of the sidebar: which workspace, which docs site, and the
 * menu behind them.
 *
 * One control for all of the "where am I acting" questions, on the GitBook
 * anatomy: the mark, the two names, a chevron, and a menu holding the moves
 * that used to be four separate controls — the workspace `<select>`, the docs
 * site `<select>`, the theme button and the sign-out that only Settings had.
 * The top bar loses all four, which is most of what makes it calm.
 *
 * The menu is a dialog in the ARIA sense — it holds the keyboard while it is
 * open and Escape closes it — because that is the one overlay contract this
 * product already keeps everywhere else (`useDialog`).
 */
export function Switcher({
  workspaceName,
  siteName,
  workspaces,
  currentWorkspace,
  onSwitchWorkspace,
  projects,
  project,
  onSwitchProject,
  mayManageSites,
  mayAddSite,
  onNewSite,
  onUpgrade,
  onSettings,
  onInvite,
  theme,
  onTheme,
  onSignOut,
}: {
  workspaceName: string
  siteName: string | null
  /** Every workspace this person belongs to; the menu lists them when >1. */
  workspaces: Workspace[]
  currentWorkspace: string | null
  onSwitchWorkspace: (slug: string) => void
  projects: ProjectSummary[]
  project: string | null
  onSwitchProject: (slug: string) => void
  /** Permission gate. People who cannot manage sites are not sold a control they cannot use. */
  mayManageSites: boolean
  /** Plan gate. False keeps the capability visible and sends it to the plan comparison. */
  mayAddSite: boolean
  onNewSite: () => void
  onUpgrade: () => void
  onSettings: () => void
  onInvite: () => void
  theme: Theme
  onTheme: (next: Theme) => void
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const panel = useDialog<HTMLDivElement>(open, () => setOpen(false))
  const close = () => setOpen(false)
  /** Close first, then act: every action here replaces what is under the menu. */
  const pick = (act: () => void) => () => {
    close()
    act()
  }

  const THEMES: { id: Theme; icon: 'monitor' | 'sun' | 'moon'; label: string }[] = [
    { id: 'system', icon: 'monitor', label: 'System' },
    { id: 'light', icon: 'sun', label: 'Light' },
    { id: 'dark', icon: 'moon', label: 'Dark' },
  ]

  return (
    <div className="switcher">
      <button
        className="switcher__button"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-switcher
        onClick={() => setOpen((v) => !v)}
        data-tip={siteName ? `${workspaceName} — ${siteName}` : workspaceName}
      >
        <span className="brand__mark">
          <Icon name="logo" size={13} />
        </span>
        <span className="switcher__names">
          <span className="switcher__ws truncate">{workspaceName}</span>
          {siteName && <span className="switcher__site truncate">{siteName}</span>}
        </span>
        <Icon name="chevron-down" size={12} className="switcher__chevron" />
      </button>

      {open && (
        <>
          <div className="popover-scrim" onMouseDown={close} />
          <div
            className="switcher__menu"
            role="dialog"
            aria-label="Workspace and docs site"
            ref={panel}
          >
            {projects.length > 0 && (
              <>
                <div className="label switcher__label">Docs sites</div>
                {projects.map((p) => (
                  <button
                    key={p.slug}
                    /* Named, so a gate can say WHICH site rather than which
                       row of a menu that also holds workspaces and a theme. */
                    data-site={p.slug}
                    className={`switcher__item${p.slug === project ? ' is-on' : ''}`}
                    onClick={pick(() => onSwitchProject(p.slug))}
                  >
                    <Icon name="pages" size={13} />
                    <span className="truncate">{p.name}</span>
                    {p.slug === project && <Icon name="check" size={13} />}
                  </button>
                ))}
                {mayManageSites &&
                  (mayAddSite ? (
                    <button className="switcher__item" data-new-site onClick={pick(onNewSite)}>
                      <Icon name="plus" size={13} />
                      New docs site
                    </button>
                  ) : (
                    <button
                      className="switcher__item"
                      data-new-site-locked
                      onClick={pick(onUpgrade)}
                    >
                      <Icon name="lock" size={13} />
                      Another docs site · upgrade
                    </button>
                  ))}
              </>
            )}

            {workspaces.length > 1 && (
              <>
                <div className="label switcher__label">Workspaces</div>
                {workspaces.map((w) => (
                  <button
                    key={w.id}
                    /* Named, for the reason the sites above are — see `data-site`. */
                    data-workspace={w.slug}
                    className={`switcher__item${w.slug === currentWorkspace ? ' is-on' : ''}`}
                    onClick={pick(() => onSwitchWorkspace(w.slug))}
                  >
                    <Icon name="folder" size={13} />
                    <span className="truncate">{w.name}</span>
                    {w.slug === currentWorkspace && <Icon name="check" size={13} />}
                  </button>
                ))}
              </>
            )}

            <div className="switcher__rule" />
            <button className="switcher__item" onClick={pick(onSettings)}>
              <Icon name="nav" size={13} />
              Settings
            </button>
            <button className="switcher__item" onClick={pick(onInvite)}>
              <Icon name="people" size={13} />
              Invite your team
            </button>

            <div className="switcher__theme">
              <span className="label">Theme</span>
              <span className="switcher__themes">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    className={`btn btn--ghost btn--icon btn--sm${theme === t.id ? ' is-active' : ''}`}
                    aria-pressed={theme === t.id}
                    data-tip={`Theme: ${t.label.toLowerCase()}`}
                    onClick={() => onTheme(t.id)}
                  >
                    <Icon name={t.icon} size={13} label={t.label} />
                  </button>
                ))}
              </span>
            </div>

            <div className="switcher__rule" />
            <button className="switcher__item" onClick={pick(onSignOut)}>
              <Icon name="close" size={13} />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  )
}
